import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import useOffer from 'hooks/Offers/useOffer'
import { useOrdersByDepartureStatus } from 'hooks/useOrdersByDepartureStatus'
import { getBedbankItems, getHotelItems } from 'supportAssistant/utils/orderUtils'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR } from 'constants/dateFormats'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import { ChatActions } from 'api/supportAssistant'
import LesaHotelItemSummary from '../../SelectPurchase/LesaPurchaseSummary/LesaHotelItemSummary'
import LesaSelectableItemCard from '../../SelectPurchase/LesaPurchaseSummary/LesaSelectableItemCard'
import LesaPurchaseSummary from '../../SelectPurchase/LesaPurchaseSummary/LesaPurchaseSummary'

/**
 * Renders the assistant's booking/item/room selection options as cards instead of pills.
 *
 * The backend sends the same options it always did — the pill's `value` is untouched — plus the
 * ids needed to find the thing in the order store. Selecting a card posts that same `value` back
 * as `agentPillValue`, so the workflow cannot tell a card tap from a button tap.
 *
 * Every product the assistant can offer has a card: flash accommodation keeps its rich item
 * summary, bedbank items, bedbank rooms, experiences and flights use the shared selectable-item
 * card, and a booking-level option renders the same purchase summary the booking list uses. The
 * button remains only as the fallback for an option whose order has not reached the store yet.
 */

interface Props {
  pills: Array<App.AgentPill>
  onSelect: (pill: App.AgentPill) => void
}

// LesaHotelItemSummary reads this only to pick between SELECT_ORDER_ITEM variants, which the
// onSelect override replaces. Hoisted so it is not a fresh object on every render.
const NO_COMPONENTS = new Map<string | undefined, App.ChatMessageMetaContent>()

// LesaHotelPurchaseSummary only draws the order card when its components map names a list-orders
// content type — it is what tells the summary "you are being rendered as a picker". A booking-level
// selection card is exactly that, so it has to be present or a flash booking renders as an empty
// turn. The action inside is never dispatched: onSelect replaces it.
const BOOKING_CARD_COMPONENTS = new Map<string | undefined, App.ChatMessageMetaContent>([
  ['list-orders', { type: 'list-orders', action: { key: ChatActions.SELECT_ORDER, type: 'client' } }],
])

const isCardPill = (pill: App.AgentPill) => !!pill.orderId || !!pill.itemId || !!pill.roomId

export const hasSelectionCards = (pills: Array<App.AgentPill>) => pills.some(isCardPill)

function PillButton({ pill, onSelect }: { pill: App.AgentPill, onSelect: (pill: App.AgentPill) => void }) {
  return (
    <TextButton
      kind="secondary"
      variant="default"
      onClick={() => onSelect(pill)}
      data-support-assistant-agent-pill={pill.value ?? pill.text}
    >
      {pill.text}
    </TextButton>
  )
}

interface SelectionCardProps {
  pill: App.AgentPill
  order: App.Order
  /**
   * The option's position in the list the assistant offered, and the only safe basis for a "Room N"
   * label. The store holds rooms and items the assistant deliberately left out — a cancelled room,
   * an ineligible item — so numbering by store position would disagree with the numbering the
   * assistant itself used.
   */
  position: number
  totalCards: number
  onSelect: (pill: App.AgentPill) => void
}

function SelectionCard({ pill, order, position, totalCards, onSelect }: SelectionCardProps) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const [offer] = useOffer<App.Offer>(order.items[0]?.offerId)

  const select = () => onSelect(pill)
  const formatDate = (value?: string) => (value ? dateFormatter(value, INTL_SHORT_DAY_NAME_DAY_MONTH_YEAR) : undefined)
  const numberedRoomLabel = intl.formatMessage({
    id: 'prsGRM9_GWeLfNUXQRbE',
    defaultMessage: 'Room {index}',
    description: 'Label identifying a specific hotel room by its index in the booking summary',
  }, { index: position + 1 })

  // Booking-level option: no item or room, so render the same summary the booking list draws. It
  // routes to the right card for every product type on its own.
  if (!pill.itemId && !pill.roomId) {
    return (
      <LesaPurchaseSummary
        order={order}
        componentsMap={BOOKING_CARD_COMPONENTS}
        onSelect={select}
      />
    )
  }

  // Room level next: a room pill also carries its item and order ids, so the deepest id wins.
  if (pill.roomId) {
    const bedbankItems = getBedbankItems(order)
    const item = bedbankItems.find(bedbankItem => bedbankItem.id === pill.itemId) ?? bedbankItems[0]
    const room = item?.rooms.find(bedbankRoom => bedbankRoom.roomId === pill.roomId)

    if (!item || !room) {
      return <PillButton pill={pill} onSelect={onSelect} />
    }

    const guestName = [room.guestFirstName, room.guestLastName].filter(Boolean).join(' ')

    return (
      <LesaSelectableItemCard
        order={order}
        label={numberedRoomLabel}
        title={room.name}
        // The lead guest sits directly under the room name because rooms of one booking usually
        // share both name and occupancy — without it, a two-room booking renders two identical
        // cards. Some suppliers omit it, which is why the room number is always shown too.
        subtitle={guestName || undefined}
        detail={room.occupancy ? formatOccupantsShort(room.occupancy) : undefined}
        detailIcon={<LineUsersAltIcon colour="primary" size="XS" />}
        // A bedbank room carries no image of its own, so it borrows the property's — the same
        // fallback LesaHotelItemSummary makes when a room type has no picture.
        imageId={item.offer?.image?.id}
        bookingNumber={item.bookingNumber}
        onSelect={select}
      />
    )
  }

  const hotelItem = getHotelItems(order).find(item => item.id === pill.itemId)
  if (hotelItem && offer) {
    return (
      <LesaHotelItemSummary
        item={hotelItem}
        order={order}
        offer={offer}
        roomLabel={totalCards === 1 ? intl.formatMessage({
          id: 'kAO4lMf8TrDze9_KuOe-',
          defaultMessage: 'Your Room',
          description: 'Label for single room in hotel purchase on the hotel or room booking page',
        }) : numberedRoomLabel}
        componentsMap={NO_COMPONENTS}
        onSelect={select}
      />
    )
  }

  const bedbankItem = getBedbankItems(order).find(item => item.id === pill.itemId)
  if (bedbankItem) {
    const nights = [formatDate(bedbankItem.checkIn?.format?.()), formatDate(bedbankItem.checkOut?.format?.())]
      .filter(Boolean)
      .join(' – ')

    return (
      <LesaSelectableItemCard
        order={order}
        label={intl.formatMessage({
          id: 'swS9zfpMwGT24nOdDYH2',
          defaultMessage: 'Hotel',
          description: 'Product tag on a selectable bedbank hotel item card in the support assistant',
        })}
        title={bedbankItem.offer?.name ?? ''}
        subtitle={bedbankItem.offer?.property?.name}
        detail={nights || undefined}
        detailIcon={<LineCalendarIcon size="XS" />}
        imageId={bedbankItem.offer?.image?.id}
        bookingNumber={bedbankItem.bookingNumber}
        onSelect={select}
      />
    )
  }

  const experienceItem = order.experienceItems.find(item => item.id === pill.itemId)
  if (experienceItem) {
    return (
      <LesaSelectableItemCard
        order={order}
        label={intl.formatMessage({
          id: 'TYnKkmANBHkNHaZtZI8P',
          defaultMessage: 'Experience',
          description: 'Product tag on a selectable experience item card in the support assistant',
        })}
        title={experienceItem.title}
        detail={formatDate(experienceItem.date)}
        detailIcon={<LineCalendarIcon size="XS" />}
        bookingNumber={experienceItem.bookingNumber}
        onSelect={select}
      />
    )
  }

  const flightItem = order.flightItems.find(item => item.id === pill.itemId)
  if (flightItem) {
    const route = [flightItem.departingAirport, flightItem.arrivalAirport].filter(Boolean).join(' → ')

    return (
      <LesaSelectableItemCard
        order={order}
        label={intl.formatMessage({
          id: 'cRElb-hsS2eEcIF0fFAK',
          defaultMessage: 'Flights',
          description: 'Addon label for flights in hotel purchase summary in the customer support assistant',
        })}
        title={route || flightItem.validatingCarrierName || ''}
        subtitle={route ? flightItem.validatingCarrierName : undefined}
        detail={formatDate(flightItem.departureDate)}
        detailIcon={<LinePlaneDepartureIcon size="XS" />}
        bookingNumber={flightItem.pnrId}
        onSelect={select}
      />
    )
  }

  return <PillButton pill={pill} onSelect={onSelect} />
}

function LesaSelectionCards({ pills, onSelect }: Props) {
  const { upcoming, previous, cancelled } = useOrdersByDepartureStatus()

  // Cancelled orders sit in their own bucket that the booking list never renders, but the assistant
  // can legitimately offer one (naming a cancelled booking is one of its answers), so search there
  // too rather than dropping the card to a button.
  const findOrder = (orderId?: string) =>
    orderId ? [...upcoming, ...previous, ...cancelled].find(order => order.id === orderId) : undefined

  return (
    <Group direction="vertical" gap={8}>
      {pills.map((pill, index) => {
        const order = findOrder(pill.orderId)
        const key = `${pill.value ?? pill.text}-${index}`

        if (!order) {
          return <PillButton key={key} pill={pill} onSelect={onSelect} />
        }

        return (
          <SelectionCard
            key={key}
            pill={pill}
            order={order}
            position={index}
            totalCards={pills.length}
            onSelect={onSelect}
          />
        )
      })}
    </Group>
  )
}

export default LesaSelectionCards
